import type { ChangeMentoringSubscriptionOutputDTO } from '@algoacademy/backend/modules/payment/endpoints/mutations/change-mentoring-subscription/output.dto';
import { MENTORING_OFFER_EMAIL_DELIVERY, type UpsertMentoringOfferOutputDTO } from '@algoacademy/backend/modules/payment/endpoints/mutations/upsert-mentoring-offer/output.dto';
import { PAYMENT__BILLING_CADENCE_DESCRIPTIONS, PAYMENT__DISCOUNT_SHAPE_DESCRIPTIONS, PAYMENT__MENTORING_TYPE_DESCRIPTIONS } from '@algoacademy/backend/modules/payment/enums';
import { PAYMENT__PERMISSIONS } from '@algoacademy/backend/modules/payment/permissions';
import { toast } from '@algoacademy/design-system/web/feedback/Toaster/Toaster';
import { showTrpcMutationErrorToast } from '@algoacademy/design-system/web/feedback/Toaster/ToasterError';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/app/_auth/useAuth';
import { useTRPC } from '@/app/_trpc/config';
import { type MentoringDiscountDraft, type MentoringDiscountInput, toMentoringDiscountInput } from '../../../_components/DiscountControls';
import { readDiscountPreviewFigures } from '../../../_components/DiscountControls/_helpers/discount-preview';
import type { FormFeedbackModel } from '../_components/FormFeedback/form-feedback-model';
import { readChangeConfirmationMode, readProductTerms } from '../_helpers/change-confirmation';
import { GROSZE_PER_ZLOTY, isStudentEmailReadyForLookup, normalizeStudentEmailForLookup } from '../_helpers/constants';
import { describeDiscountInForce } from '../_helpers/discount-comparison';
import { readDiscountPrefill } from '../_helpers/discount-prefill';
import { readDroppedDiscount } from '../_helpers/dropped-discount';
import { calculateRenewalPriceZloty, formatDiscountSummary, formatTermsSummary, type MentoringOfferProduct } from '../_helpers/mentoring-terms';
import { readShownOfferLink } from '../_helpers/offer-link';
import { readRefusal } from '../_helpers/refusal';
import { MENTORING_OFFER_FORM_DEFAULTS, type MentoringOfferFormSchema, mentoringOfferFormSchemaZ } from '../schema';
type UseMentoringOfferFormArgs = {
  mentoringProduct: MentoringOfferProduct;
  discountDraft: MentoringDiscountDraft;
  onDiscountChange: (nextDraft: MentoringDiscountDraft) => void;
};
export function useMentoringOfferForm({
  mentoringProduct,
  discountDraft,
  onDiscountChange
}: UseMentoringOfferFormArgs) {
  const {
    user
  } = useAuth();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  /**
   * Keeps the just-issued result visible across re-renders. On a later lookup,
   * the query supplies the persisted unpaid link instead.
   */
  const [issuedOffer, setIssuedOffer] = useState<{
    studentEmail: string;
    result: UpsertMentoringOfferOutputDTO;
  } | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  /**
   * A validated offer waiting on the drop-discount confirmation below - see
   * readDroppedDiscount. Only ever set for a submission that would wipe the
   * discount on the student's unpaid offer; every other submit goes straight
   * to submitOffer without touching this. Tagged with the address it was
   * raised for, same as issuedOffer and changeOutcome above.
   */
  const [pendingOffer, setPendingOffer] = useState<{
    studentEmail: string;
    values: MentoringOfferFormSchema;
  } | null>(null);
  /**
   * The address whose discount was already copied into the draft, so one lookup
   * fills the controls once. Staff usually prices the discount before typing the
   * e-mail, and a second copy would throw that work away - see readDiscountPrefill.
   */
  const [prefilledDiscountFor, setPrefilledDiscountFor] = useState<string | null>(null);
  /**
   * Tagged with the address it belongs to, so retyping the e-mail cannot leave
   * one student's outcome sitting under another student's subscription.
   */
  const [changeOutcome, setChangeOutcome] = useState<{
    studentEmail: string;
    result: ChangeMentoringSubscriptionOutputDTO;
  } | null>(null);
  const upsertMentoringOffer = useMutation(trpc.payment.upsertMentoringOffer.mutationOptions());
  const changeMentoringSubscription = useMutation(trpc.payment.changeMentoringSubscription.mutationOptions());
  const form = useForm<MentoringOfferFormSchema>({
    defaultValues: MENTORING_OFFER_FORM_DEFAULTS,
    mode: 'onSubmit',
    resolver: zodResolver(mentoringOfferFormSchemaZ)
  });
  const hasPaymentsPermission = user.hasPermissions([PAYMENT__PERMISSIONS.PAYMENTS.MANAGE.ENABLED]);

  // The backend keys students on the lowercased address; the lookup has to ask
  // about exactly the same string the mutations will write.
  const rawStudentEmail = form.watch('studentEmail');
  const studentEmail = normalizeStudentEmailForLookup(rawStudentEmail);
  const isStudentEmailValid = isStudentEmailReadyForLookup(rawStudentEmail);
  const mentoringStateQuery = useQuery(trpc.payment.getStudentMentoringState.queryOptions({
    studentEmail
  }, {
    enabled: hasPaymentsPermission && isStudentEmailValid
  }));
  const mentoringState = mentoringStateQuery.data;
  const current = mentoringState?.current ?? null;
  /**
   * The discount this student already has: the one they pay today, or the one
   * the unpaid link is waiting to charge. Only one of the two can exist at once.
   */
  const discountInForce = current?.discount ?? mentoringState?.unpaidOffer?.discount ?? null;
  const discountPrefill = readDiscountPrefill({
    studentEmail,
    discountInForce: mentoringStateQuery.isPending ? undefined : discountInForce,
    prefilledFor: prefilledDiscountFor
  });

  /**
   * The student's own discount, copied into the controls one commit after the
   * lookup found it. It cannot happen during the render that found it: the draft
   * lives in the card above now, and React refuses a parent's state update from a
   * child's render. A frame is nothing next to the round trip that just ran.
   */
  useEffect(() => {
    if (discountPrefill === null) return;
    setPrefilledDiscountFor(studentEmail);
    onDiscountChange(discountPrefill);
  }, [discountPrefill, studentEmail, onDiscountChange]);

  /**
   * A stashed offer belongs to one e-mail. Staff can leave the drop-discount
   * alert open and retype the address underneath it, and confirming would
   * then issue those old values against a student nobody is looking at any
   * more. Dropping the stash the moment the address no longer matches the
   * tag it was raised for keeps every confirm bound to the e-mail that
   * raised it, and closes the alert along with it - isOpen reads off the
   * same state.
   */
  useEffect(() => {
    setPendingOffer(prev => prev !== null && prev.studentEmail !== studentEmail ? null : prev);
  }, [studentEmail]);
  const productTerms = readProductTerms(mentoringProduct);
  const {
    mentoringType,
    billingCadence,
    hoursWithMentor
  } = productTerms;
  const chosenTermsSummary = formatTermsSummary({
    mentoringTypeLabel: PAYMENT__MENTORING_TYPE_DESCRIPTIONS[mentoringType],
    billingCadenceLabel: PAYMENT__BILLING_CADENCE_DESCRIPTIONS[billingCadence],
    hoursWithMentor: hoursWithMentor ?? null
  });
  const termsInForceSummary = current === null ? null : formatTermsSummary({
    mentoringTypeLabel: current.mentoringTypeLabel,
    billingCadenceLabel: current.billingCadenceLabel,
    hoursWithMentor: current.hoursWithMentor
  });
  /**
   * Null, never undefined: this form always means the price it shows, so a
   * full-price draft is how an offer or a subscription drops the discount it was
   * carrying. There is no "no opinion" state left to send.
   */
  const chosenDiscount: MentoringDiscountInput | null = toMentoringDiscountInput(discountDraft);
  const confirmationMode = readChangeConfirmationMode({
    current,
    scheduledChange: mentoringState?.scheduledChange ?? null,
    productTerms,
    chosenDiscount
  });
  const isLookupPending = isStudentEmailValid && (mentoringStateQuery.isPending || mentoringStateQuery.isFetching && mentoringStateQuery.data === undefined);
  const isChangeMode = mentoringState?.subscriptionState === 'changeable' && current !== null;
  const isBlocked = (mentoringState?.subscriptionState === 'unpaidRenewal' || mentoringState?.subscriptionState === 'cancelled') && current !== null;
  const shownOutcome = changeOutcome !== null && changeOutcome.studentEmail === studentEmail ? changeOutcome.result : null;
  const submittedOffer = issuedOffer !== null && issuedOffer.studentEmail === studentEmail ? issuedOffer.result : null;

  /**
   * The link block has to survive both a saved subscription change and a
   * saved offer: neither writes anything that unmounts it, and `current`
   * keeps its `offerUrl` across the refetch a change triggers, so reading
   * straight from what the query and the last submission already hold is
   * enough - no extra state to carry the link past a save.
   */
  const shownOffer = readShownOfferLink(submittedOffer, mentoringState?.unpaidOffer, current);

  /**
   * What the first invoice after the renewal actually charges. With a discount
   * on it that is not the price book's figure, and it is priced by the very
   * module checkout uses, so the confirmation cannot quote a number the buyer
   * will never see.
   */
  const renewalPriceZloty = chosenDiscount === null ? calculateRenewalPriceZloty(mentoringProduct) : readDiscountPreviewFigures({
    mentoringProduct,
    discount: chosenDiscount
  }).discountedInvoiceGrosze / GROSZE_PER_ZLOTY;

  /**
   * The discount being confirmed, said the way the panel above says the one in
   * force. Nothing has run yet, so every period staff typed is still ahead.
   */
  const confirmedDiscountSummary = chosenDiscount !== null ? formatDiscountSummary({
    shape: chosenDiscount.shape,
    shapeLabel: PAYMENT__DISCOUNT_SHAPE_DESCRIPTIONS[chosenDiscount.shape],
    value: chosenDiscount.value,
    periodCount: chosenDiscount.periodCount,
    remainingPeriods: chosenDiscount.periodCount,
    reason: chosenDiscount.reason ?? null
  }, PAYMENT__BILLING_CADENCE_DESCRIPTIONS[billingCadence]) : current !== null && current.discount !== null ? 'Zniżka znika. Od odnowienia uczeń płaci pełną cenę z cennika.' : undefined;

  /**
   * Which discount, if any, submitting this offer right now would wipe - see
   * readDroppedDiscount. Read here, not inside onSubmit, so the same value
   * both decides the branch and feeds the alert's text below.
   */
  const droppedDiscount = readDroppedDiscount({
    submittedDiscount: chosenDiscount,
    unpaidOfferDiscount: mentoringState?.unpaidOffer?.discount
  });
  const droppedDiscountSummary = droppedDiscount === null ? null : formatDiscountSummary(droppedDiscount, PAYMENT__BILLING_CADENCE_DESCRIPTIONS[billingCadence]);
  const discountInForceDescription = discountInForce === null ? null : describeDiscountInForce(discountInForce, current?.billingCadenceLabel ?? PAYMENT__BILLING_CADENCE_DESCRIPTIONS[billingCadence]);

  /**
   * The one place `upsertMentoringOffer` is called - straight from onSubmit
   * when nothing is at risk, or from the drop-discount alert's confirm once
   * staff has seen what disappears.
   */
  const submitOffer = (values: MentoringOfferFormSchema) => {
    setRefusal(null);
    upsertMentoringOffer.mutate({
      mentoringType,
      billingCadence,
      hoursWithMentor,
      buyerEmail: values.buyerEmail,
      buyerName: values.buyerName,
      studentEmail: values.studentEmail,
      studentName: values.studentName,
      discount: chosenDiscount
    }, {
      onSuccess: async result => {
        setIssuedOffer({
          studentEmail,
          result
        });
        setPendingOffer(null);
        if (result.emailDelivery === MENTORING_OFFER_EMAIL_DELIVERY.SENT) {
          toast.success('Oferta gotowa. E-mail z linkiem został wysłany kupującemu.');
        } else {
          toast.error('Oferta gotowa, ale e-mail nie został wysłany', {
            description: 'Link pozostaje dostępny pod formularzem. Przekaż go ręcznie.'
          });
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.payment.getStudentMentoringState.queryKey({
            studentEmail
          })
        });
      },
      onError: error => {
        setPendingOffer(null);
        setRefusal(readRefusal(error));
        showTrpcMutationErrorToast(error);
      }
    });
  };
  const onSubmit = (values_0: MentoringOfferFormSchema) => {
    if (droppedDiscount !== null) {
      // Ask first: submitting at list price would overwrite the discount
      // columns on the student's unpaid offer outright, and retyping the
      // offer afterwards would never bring that discount back. Validation
      // already passed, so the values just wait for staff's confirmation.
      setPendingOffer({
        studentEmail,
        values: values_0
      });
      return;
    }
    submitOffer(values_0);
  };
  const onConfirmChange = () => {
    setRefusal(null);
    changeMentoringSubscription.mutate({
      studentEmail,
      mentoringType,
      billingCadence,
      hoursWithMentor,
      discount: chosenDiscount
    }, {
      onSuccess: async result_0 => {
        setIsConfirmOpen(false);
        setChangeOutcome({
          studentEmail,
          result: result_0
        });
        if (result_0.outcome === 'unchanged') {
          toast.info('Ustawienia mentoringu się nie zmieniły.');
        } else {
          toast.success('Zapisano. Sprawdź podsumowanie pod formularzem.');
        }

        // The panel above now describes the old truth - refetch it.
        await queryClient.invalidateQueries({
          queryKey: trpc.payment.getStudentMentoringState.queryKey({
            studentEmail
          })
        });
      },
      onError: error_0 => {
        setIsConfirmOpen(false);
        setRefusal(readRefusal(error_0));
        showTrpcMutationErrorToast(error_0);
      }
    });
  };
  const scheduledChangeForPrice = mentoringState?.scheduledChange ?? null;
  const changeConfirmNewPriceZloty = confirmationMode === 'unchangedScheduled' && scheduledChangeForPrice !== null ?
  // The waiting change froze what its first invoice charges, discount included.
  scheduledChangeForPrice.priceGrosze / GROSZE_PER_ZLOTY : renewalPriceZloty;
  const isUpsertPending = upsertMentoringOffer.isPending;
  const isChangePending = changeMentoringSubscription.isPending;
  const feedback = {
    refusal,
    shownOutcome,
    shownOffer,
    dropDiscount: {
      pendingOffer,
      setPendingOffer,
      submitOffer,
      isPending: isUpsertPending,
      summary: droppedDiscountSummary
    },
    changeConfirm: {
      isChangeMode,
      current,
      isOpen: isConfirmOpen,
      setIsOpen: setIsConfirmOpen,
      onConfirm: onConfirmChange,
      isPending: isChangePending,
      termsInForceSummary,
      chosenTermsSummary,
      renewalPriceZloty: changeConfirmNewPriceZloty,
      discountSummary: confirmedDiscountSummary,
      mode: confirmationMode
    }
  } satisfies FormFeedbackModel;
  return {
    hasPaymentsPermission,
    form,
    mentoringProduct,
    discountDraft,
    onDiscountChange,
    isChangeMode,
    isBlocked,
    isLookupPending,
    isStudentEmailValid,
    chosenTermsSummary,
    mentoringState,
    current,
    discountInForceDescription,
    onSubmit,
    isUpsertPending,
    isChangePending,
    setIsConfirmOpen,
    feedback
  };
}
export type MentoringOfferFormContext = ReturnType<typeof useMentoringOfferForm>;